import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { CheckCircle2, HeartPulse } from "lucide-react";
import { api, errMsg } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Checkbox } from "../../components/ui/checkbox";
import { Field } from "../../components/Common";
import PublicFrame, { usePublicBranding } from "./PublicFrame";

const NONE = ["None of these", "Nessuna di queste"];
const T = {
  en: { yes: "Yes", no: "No", req: "Please give details (required)", opt: "Details (optional)", eyebrow: "Health questionnaire", title: "Before your visit",
    intro: "Your answers are confidential and only shared with your practitioners, so they can treat you safely. It takes about 3 minutes.",
    confirm: "I confirm this information is accurate and I will tell the clinic about any changes to my health before treatment.", name: "Full name",
    left: (n) => `${n} question${n === 1 ? "" : "s"} still to answer`, send: "Send questionnaire", sending: "Sending…", thanks: "Thank you",
    sent: "Your health questionnaire has been sent to the clinic.", change: "If anything changes before your visit, update it from your patient area or tell the clinic." },
  it: { yes: "Sì", no: "No", req: "Indica i dettagli (obbligatorio)", opt: "Dettagli (facoltativo)", eyebrow: "Questionario sanitario", title: "Prima della visita",
    intro: "Le tue risposte sono riservate e condivise solo con i tuoi professionisti, per trattarti in sicurezza. Richiede circa 3 minuti.",
    confirm: "Confermo che le informazioni sono corrette e che comunicherò alla clinica ogni cambiamento del mio stato di salute prima del trattamento.", name: "Nome e cognome",
    left: (n) => `${n} domand${n === 1 ? "a" : "e"} ancora da completare`, send: "Invia questionario", sending: "Invio…", thanks: "Grazie",
    sent: "Il questionario sanitario è stato inviato alla clinica.", change: "Se qualcosa cambia prima della visita, aggiornalo dalla tua area paziente o avvisa la clinica." },
};

function YesNo({ q, value, onChange, l }) {
  const v = value || {};
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-2">
        {["yes", "no"].map((x) => (
          <button key={x} type="button" data-testid={`hq-${q.key}-${x}`} onClick={() => onChange({ answer: x, details: x === "yes" ? v.details || "" : "" })}
            className={`rounded-full border px-5 py-1.5 text-sm font-medium transition-colors ${v.answer === x ? "bg-primary text-primary-foreground border-primary" : "bg-card hover:border-primary"}`}>{l[x]}</button>
        ))}
      </div>
      {v.answer === "yes" && <Textarea rows={2} data-testid={`hq-${q.key}-details`} placeholder={q.details_required ? l.req : l.opt} value={v.details || ""} onChange={(e) => onChange({ ...v, details: e.target.value })} />}
    </div>
  );
}

function Multi({ q, value, onChange }) {
  const sel = value || [];
  const toggle = (o) => {
    if (NONE.includes(o)) return onChange(sel.includes(o) ? [] : [o]);
    const rest = sel.filter((x) => !NONE.includes(x));
    return onChange(rest.includes(o) ? rest.filter((x) => x !== o) : [...rest, o]);
  };
  return (
    <div className="grid sm:grid-cols-2 gap-2">
      {q.options.map((o, i) => <label key={o} className="flex items-center gap-2 text-sm"><Checkbox data-testid={`hq-${q.key}-opt-${i}`} checked={sel.includes(o)} onCheckedChange={() => toggle(o)} />{o}</label>)}
    </div>
  );
}

function Question({ q, value, onChange, l }) {
  return (
    <div className="space-y-2 py-4 border-b last:border-0" data-testid={`hq-question-${q.key}`}>
      <p className="text-sm font-medium">{q.label}</p>
      {q.type === "yesno" && <YesNo q={q} value={value} onChange={onChange} l={l} />}
      {q.type === "multi" && <Multi q={q} value={value} onChange={onChange} />}
      {q.type === "text" && <Input data-testid={`hq-${q.key}-input`} value={value || ""} onChange={(e) => onChange(e.target.value)} />}
    </div>
  );
}

export function HealthFormBody({ token, onDone, embedded }) {
  const [data, setData] = useState(null);
  const [lang, setLang] = useState("");
  const [answers, setAnswers] = useState({});
  const [name, setName] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  useEffect(() => {
    api.get(`/public/health/${token}`, { params: { lang: lang || undefined } }).then((r) => {
      setData(r.data); setName((n) => n || r.data.client_name);
      setAnswers((a) => ({ ...Object.fromEntries(Object.entries(a).filter(([k]) => k !== "conditions")), ...r.data.answers }));
    }).catch((e) => setError(errMsg(e)));
  }, [token, lang]);
  usePublicBranding(embedded ? null : data);
  if (!data) return <p data-testid="hq-error">{error || "…"}</p>;
  const l = T[data.language] || T.en;
  const sections = [...new Set(data.questions.map((q) => q.section))];
  const unanswered = data.questions.filter((q) => q.type === "yesno" && !answers[q.key]?.answer).length;
  const submit = async () => {
    setBusy(true); setError("");
    try { await api.post(`/public/health/${token}`, { answers, signer_name: name, confirmed, language: data.language }); setDone(true); window.scrollTo(0, 0); onDone?.(); }
    catch (e) { setError(errMsg(e)); } finally { setBusy(false); }
  };
  if ((done || data.status === "completed") && !embedded) {
    return (
      <div className="py-16 text-center space-y-3" data-testid="hq-done">
        <CheckCircle2 className="h-12 w-12 text-primary mx-auto" /><h1 className="text-3xl font-bold">{l.thanks}</h1>
        <p className="text-muted-foreground">{l.sent}</p><p className="text-sm text-muted-foreground">{l.change}</p>
      </div>
    );
  }
  return (
    <div className="space-y-6" data-testid="hq-form">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground flex items-center gap-2"><HeartPulse className="h-3.5 w-3.5" />{l.eyebrow}</p>
          <h1 className="text-3xl sm:text-4xl font-bold mt-1">{l.title}</h1>
        </div>
        <div className="flex gap-1.5" data-testid="hq-lang-switch">
          {["en", "it"].map((x) => <button key={x} data-testid={`hq-lang-${x}`} onClick={() => setLang(x)} className={`rounded-full border px-3 py-1 text-xs ${data.language === x ? "bg-primary text-primary-foreground" : ""}`}>{x === "en" ? "English" : "Italiano"}</button>)}
        </div>
      </div>
      <p className="text-sm text-muted-foreground -mt-3">{l.intro}</p>
      {sections.map((s) => (
        <section key={s} className="rounded-[var(--radius)] border bg-card px-5 sm:px-6 py-2">
          <h2 className="text-base font-semibold pt-4">{s}</h2>
          {data.questions.filter((q) => q.section === s).map((q) => <Question key={q.key} q={q} l={l} value={answers[q.key]} onChange={(v) => setAnswers({ ...answers, [q.key]: v })} />)}
        </section>
      ))}
      <div className="rounded-[var(--radius)] border bg-card p-5 space-y-4">
        <label className="flex items-start gap-3 text-sm"><Checkbox className="mt-0.5" data-testid="hq-confirm-checkbox" checked={confirmed} onCheckedChange={(v) => setConfirmed(!!v)} />{l.confirm}</label>
        <Field label={l.name}><Input data-testid="hq-name-input" value={name} onChange={(e) => setName(e.target.value)} /></Field>
        {error && <p className="text-sm text-destructive" data-testid="hq-submit-error">{error}</p>}
        {unanswered > 0 && <p className="text-xs text-muted-foreground" data-testid="hq-unanswered">{l.left(unanswered)}</p>}
        <Button className="w-full rounded-full h-12" data-testid="hq-submit-btn" disabled={busy || !confirmed || name.trim().length < 2 || unanswered > 0} onClick={submit}>{busy ? l.sending : l.send}</Button>
      </div>
    </div>
  );
}

export default function HealthForm() {
  const { token } = useParams();
  const [b, setB] = useState({});
  useEffect(() => { api.get(`/public/health/${token}`).then((r) => setB(r.data.branding || {})).catch(() => {}); }, [token]);
  return <PublicFrame branding={b}><HealthFormBody token={token} /></PublicFrame>;
}
